import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, Lock, ShieldCheck, CreditCard, Sparkles, ExternalLink } from 'lucide-react';
import AuthContext from '../context/AuthContext';
import { apiClient } from '../apiClient';

const SettingsPage = () => {
  const { user, setUser } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [emailForm, setEmailForm] = useState({ newEmail: '', password: '' });
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [emailStatus, setEmailStatus] = useState(null);
  const [passwordStatus, setPasswordStatus] = useState(null);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [privacySaving, setPrivacySaving] = useState(false);
  const [privacyStatus, setPrivacyStatus] = useState(null);
  const [privacySettings, setPrivacySettings] = useState({
    followers_private: user?.followers_private ?? false,
    following_private: user?.following_private ?? false
  });
  const [billingStatus, setBillingStatus] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);

  // Handle Stripe redirect results
  useEffect(() => {
    const billing = searchParams.get('billing');
    if (billing === 'success') {
      setBillingStatus({ type: 'success', message: 'Subscription activated! Welcome to Pro.' });
      setSearchParams({});
    } else if (billing === 'canceled') {
      setBillingStatus({ type: 'info', message: 'Checkout canceled. No changes made.' });
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleUpgrade = async () => {
    setBillingStatus(null);
    setBillingLoading(true);
    try {
      const { checkout_url } = await apiClient.post('/api/billing/checkout', { plan: 'pro' });
      if (checkout_url) {
        window.location.href = checkout_url;
      } else {
        setBillingStatus({ type: 'error', message: 'Unable to create checkout session.' });
      }
    } catch (error) {
      setBillingStatus({ type: 'error', message: error.message || 'Unable to start checkout.' });
    } finally {
      setBillingLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setBillingStatus(null);
    setBillingLoading(true);
    try {
      const { portal_url } = await apiClient.post('/api/billing/portal');
      if (portal_url) {
        window.location.href = portal_url;
      } else {
        setBillingStatus({ type: 'error', message: 'Unable to access billing portal.' });
      }
    } catch (error) {
      setBillingStatus({ type: 'error', message: error.message || 'Unable to open billing portal.' });
    } finally {
      setBillingLoading(false);
    }
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    setEmailStatus(null);

    const nextEmail = emailForm.newEmail.trim();
    if (!nextEmail) {
      setEmailStatus({ type: 'error', message: 'Please enter a new email address.' });
      return;
    }
    if (user?.email && nextEmail.toLowerCase() === user.email.toLowerCase()) {
      setEmailStatus({ type: 'error', message: 'That email is already your current email.' });
      return;
    }
    if (!emailForm.password) {
      setEmailStatus({ type: 'error', message: 'Please confirm your password to change email.' });
      return;
    }

    try {
      setSavingEmail(true);
      const updatedUser = await apiClient.post('/api/users/change-email', {
        new_email: nextEmail,
        password: emailForm.password
      });
      setUser(updatedUser);
      setEmailForm({ newEmail: '', password: '' });
      setEmailStatus({ type: 'success', message: 'Email updated successfully.' });
    } catch (error) {
      setEmailStatus({ type: 'error', message: error.message || 'Unable to update email.' });
    } finally {
      setSavingEmail(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordStatus(null);

    if (!passwordForm.current || !passwordForm.next) {
      setPasswordStatus({ type: 'error', message: 'Please enter your current and new password.' });
      return;
    }
    if (passwordForm.next.length < 8) {
      setPasswordStatus({ type: 'error', message: 'New password must be at least 8 characters.' });
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordStatus({ type: 'error', message: 'New password and confirmation do not match.' });
      return;
    }

    try {
      setSavingPassword(true);
      const updatedUser = await apiClient.post('/api/users/change-password', {
        current_password: passwordForm.current,
        new_password: passwordForm.next
      });
      setUser(updatedUser);
      setPasswordForm({ current: '', next: '', confirm: '' });
      setPasswordStatus({ type: 'success', message: 'Password updated successfully.' });
    } catch (error) {
      setPasswordStatus({ type: 'error', message: error.message || 'Unable to update password.' });
    } finally {
      setSavingPassword(false);
    }
  };

  const rawLevel = user?.account_level || user?.account_tier || user?.role || (user?.is_admin ? 'admin' : 'starter');
  const accountLevel = String(rawLevel || 'starter').replace(/^\w/, (c) => c.toUpperCase());

  const handlePrivacySave = async (event) => {
    event.preventDefault();
    setPrivacyStatus(null);
    try {
      setPrivacySaving(true);
      const updatedUser = await apiClient.patch('/api/users/profile', {
        followers_private: privacySettings.followers_private,
        following_private: privacySettings.following_private
      });
      setUser(updatedUser);
      setPrivacyStatus({ type: 'success', message: 'Privacy settings updated.' });
    } catch (error) {
      setPrivacyStatus({ type: 'error', message: error.message || 'Unable to update privacy.' });
    } finally {
      setPrivacySaving(false);
    }
  };

  return (
    <div className="min-h-screen settings-page">
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <h1 className="text-3xl font-bold">Settings</h1>
            <p className="text-gray-400">Manage your account details and security preferences.</p>
          </div>
        </div>
      </div>

      <div className="settings-grid">
        <div className="card-glass settings-card">
          <div className="settings-card-header">
            <Mail className="w-5 h-5 text-rebel-blue" />
            <div>
              <h2 className="text-xl font-bold">Account</h2>
              <p className="settings-meta">Update your email and account contact info.</p>
            </div>
          </div>

          <div className="modal-field">
            <label>Account Email</label>
            <input className="input-glass" type="email" value={user?.email || ''} readOnly />
          </div>
          <div className="modal-field">
            <label>Account Level</label>
            <div className={`account-level-pill ${String(rawLevel).toLowerCase() === 'admin' ? 'admin' : 'standard'}`}>
              {accountLevel}
            </div>
          </div>

          <div className="settings-divider" />

          <form className="settings-form" onSubmit={handleEmailSubmit}>
            <h3 className="text-lg font-semibold">Change Email</h3>
            {emailStatus?.message && (
              <div className={emailStatus.type === 'error' ? 'error-banner' : 'success-banner'}>
                {emailStatus.message}
              </div>
            )}
            <div className="modal-field">
              <label>New Email</label>
              <input
                className="input-glass"
                type="email"
                value={emailForm.newEmail}
                onChange={(event) => setEmailForm((prev) => ({ ...prev, newEmail: event.target.value }))}
                placeholder="you@rebelprompts.com"
              />
            </div>
            <div className="modal-field">
              <label>Current Password</label>
              <input
                className="input-glass"
                type="password"
                value={emailForm.password}
                onChange={(event) => setEmailForm((prev) => ({ ...prev, password: event.target.value }))}
                placeholder="Enter your password"
              />
            </div>
            <button className="btn-primary" type="submit" disabled={savingEmail}>
              {savingEmail ? 'Updating...' : 'Update Email'}
            </button>
          </form>
        </div>

        <div className="card-glass settings-card">
          <div className="settings-card-header">
            <CreditCard className="w-5 h-5 text-rebel-cyan" />
            <div>
              <h2 className="text-xl font-bold">Subscription</h2>
              <p className="settings-meta">Manage your plan and billing.</p>
            </div>
          </div>

          {billingStatus?.message && (
            <div className={`${billingStatus.type === 'error' ? 'error-banner' : billingStatus.type === 'info' ? 'info-banner' : 'success-banner'} mb-4`}>
              {billingStatus.message}
            </div>
          )}

          <div className="modal-field">
            <label>Current Plan</label>
            <div className="flex items-center gap-3">
              <div className={`account-level-pill ${rawLevel === 'pro' ? 'pro' : 'standard'}`}>
                {accountLevel}
              </div>
              {rawLevel === 'pro' && <Sparkles className="w-4 h-4 text-rebel-cyan" />}
            </div>
          </div>

          {rawLevel !== 'pro' && rawLevel !== 'admin' ? (
            <div className="mt-4">
              <div className="p-4 rounded-lg bg-gradient-to-r from-rebel-blue/20 to-rebel-cyan/20 border border-rebel-cyan/30 mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-rebel-cyan" />
                  Upgrade to Pro
                </h3>
                <ul className="text-sm text-gray-300 mt-2 space-y-1">
                  <li>- Unlimited prompts and projects</li>
                  <li>- Team collaboration features</li>
                  <li>- Priority support</li>
                  <li>- Advanced API access</li>
                </ul>
              </div>
              <button
                className="btn-primary w-full"
                onClick={handleUpgrade}
                disabled={billingLoading}
              >
                {billingLoading ? 'Loading...' : 'Upgrade to Pro'}
              </button>
            </div>
          ) : rawLevel === 'pro' ? (
            <div className="mt-4">
              <button
                className="btn-secondary w-full flex items-center justify-center gap-2"
                onClick={handleManageSubscription}
                disabled={billingLoading}
              >
                <ExternalLink className="w-4 h-4" />
                {billingLoading ? 'Loading...' : 'Manage Subscription'}
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Update payment method, view invoices, or cancel subscription
              </p>
            </div>
          ) : null}
        </div>

        <div className="card-glass settings-card">
          <div className="settings-card-header">
            <ShieldCheck className="w-5 h-5 text-rebel-blue" />
            <div>
              <h2 className="text-xl font-bold">Privacy</h2>
              <p className="settings-meta">Control who can see your followers and following lists.</p>
            </div>
          </div>

          <form className="settings-form" onSubmit={handlePrivacySave}>
            {privacyStatus?.message && (
              <div className={privacyStatus.type === 'error' ? 'error-banner' : 'success-banner'}>
                {privacyStatus.message}
              </div>
            )}
            <div className="settings-toggle">
              <div>
                <div className="settings-toggle-title">Hide Followers List</div>
                <div className="settings-toggle-sub">
                  Others will still see your follower count.
                </div>
              </div>
              <input
                type="checkbox"
                checked={privacySettings.followers_private}
                onChange={(event) =>
                  setPrivacySettings((prev) => ({ ...prev, followers_private: event.target.checked }))
                }
              />
            </div>
            <div className="settings-toggle">
              <div>
                <div className="settings-toggle-title">Hide Following List</div>
                <div className="settings-toggle-sub">
                  Others will still see your following count.
                </div>
              </div>
              <input
                type="checkbox"
                checked={privacySettings.following_private}
                onChange={(event) =>
                  setPrivacySettings((prev) => ({ ...prev, following_private: event.target.checked }))
                }
              />
            </div>
            <button className="btn-primary" type="submit" disabled={privacySaving}>
              {privacySaving ? 'Saving...' : 'Save Privacy Settings'}
            </button>
          </form>
        </div>

        <div className="card-glass settings-card">
          <div className="settings-card-header">
            <Lock className="w-5 h-5 text-rebel-red" />
            <div>
              <h2 className="text-xl font-bold">Security</h2>
              <p className="settings-meta">Keep your account secure with a strong password.</p>
            </div>
          </div>

          <form className="settings-form" onSubmit={handlePasswordSubmit}>
            <h3 className="text-lg font-semibold">Change Password</h3>
            {passwordStatus?.message && (
              <div className={passwordStatus.type === 'error' ? 'error-banner' : 'success-banner'}>
                {passwordStatus.message}
              </div>
            )}
            <div className="modal-field">
              <label>Current Password</label>
              <input
                className="input-glass"
                type="password"
                value={passwordForm.current}
                onChange={(event) => setPasswordForm((prev) => ({ ...prev, current: event.target.value }))}
                placeholder="Enter current password"
              />
            </div>
            <div className="modal-field">
              <label>New Password</label>
              <input
                className="input-glass"
                type="password"
                value={passwordForm.next}
                onChange={(event) => setPasswordForm((prev) => ({ ...prev, next: event.target.value }))}
                placeholder="Minimum 8 characters"
              />
            </div>
            <div className="modal-field">
              <label>Confirm New Password</label>
              <input
                className="input-glass"
                type="password"
                value={passwordForm.confirm}
                onChange={(event) => setPasswordForm((prev) => ({ ...prev, confirm: event.target.value }))}
                placeholder="Re-enter new password"
              />
            </div>
            <button className="btn-primary" type="submit" disabled={savingPassword}>
              {savingPassword ? 'Updating...' : 'Update Password'}
            </button>
          </form>

          <div className="settings-note">
            <ShieldCheck className="w-4 h-4" />
            <span>Use a unique password to keep your account safe.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
